// Прошивки RTS-панелей в Sales. Образы, их проверку и сами обновления ведёт
// Шанырак; GEO передаёт файл, показывает состояние и пишет каждое действие в
// журнал Sales. Доступ — только у админа с правом на прошивки
// (adminFirmwareAccess; право ставит seed_admin в поде).

import { gql } from "./gql";
import { getToken, logout } from "./auth";

export type FirmwareImage = {
  id: string;
  name: string;
  path: string;
  version: string;
  kind: "RELEASE" | "DEV";
  mcu: string;
  hwMin: number;
  hwMax: number;
  gitSha: string;
  size: number;
  crc: string;
  uploadedAt: string;
  uploadedBy: string | null;
};

export type FirmwarePhase = "WAITING_CONFIG" | "COMMAND_SENT" | "DONE" | "FAILED" | "CANCELED";

export type FirmwareUpdate = {
  id: string;
  target: FirmwareImage;
  fromVersion: string | null;
  phase: FirmwarePhase;
  startedAt: string;
  phaseChangedAt: string;
  finishedAt: string | null;
  attempts: number;
  lastError: string | null;
  startedBy: string | null;
};

export type PanelFirmware = {
  deviceId: string;
  hwVersion: string | null;
  hwCode: number | null;
  swVersion: string | null;
  proto: string | null;
  bootCrc: string | null;
  reportedAt: string | null;
  testbench: boolean;
  update: FirmwareUpdate | null;
};

export type FirmwareResult = {
  success: boolean;
  notice: string | null;
  failureReason: string | null;
};

const IMAGE_FIELDS = "id name path version kind mcu hwMin hwMax gitSha size crc uploadedAt uploadedBy";

const PANEL_FIELDS = `deviceId hwVersion hwCode swVersion proto bootCrc reportedAt testbench
  update { id target { ${IMAGE_FIELDS} } fromVersion phase startedAt phaseChangedAt finishedAt attempts lastError startedBy }`;

export const FIRMWARE_ACCESS_QUERY = `query AdminFirmwareAccess { adminFirmwareAccess }`;

export const FIRMWARE_IMAGES_QUERY = `query AdminFirmwareImages { adminFirmwareImages { ${IMAGE_FIELDS} } }`;

export const PANEL_FIRMWARE_QUERY = `query AdminPanelFirmware($deviceIds: [UUID!]!) {
  adminPanelFirmware(deviceIds: $deviceIds) { ${PANEL_FIELDS} }
}`;

const UPLOAD_MUTATION = `mutation AdminUploadFirmwareImage($file: Upload!) {
  adminUploadFirmwareImage(file: $file) { success notice failureReason image { ${IMAGE_FIELDS} } }
}`;

const PANEL_RESULT = `success notice failureReason firmware { ${PANEL_FIELDS} }`;

export const START_UPDATE_MUTATION = `mutation AdminStartFirmwareUpdate($deviceId: UUID!, $imageId: UUID!) {
  adminStartFirmwareUpdate(deviceId: $deviceId, imageId: $imageId) { ${PANEL_RESULT} }
}`;

export const CANCEL_UPDATE_MUTATION = `mutation AdminCancelFirmwareUpdate($deviceId: UUID!) {
  adminCancelFirmwareUpdate(deviceId: $deviceId) { ${PANEL_RESULT} }
}`;

export const SET_TESTBENCH_MUTATION = `mutation AdminSetPanelTestbench($deviceId: UUID!, $testbench: Boolean!) {
  adminSetPanelTestbench(deviceId: $deviceId, testbench: $testbench) { ${PANEL_RESULT} }
}`;

// Есть ли у вошедшего право на прошивки. Ошибка — считаем, что нет: раздел
// не показывается, а не ломает Sales.
export async function loadFirmwareAccess(): Promise<boolean> {
  try {
    const d = await gql<{ adminFirmwareAccess: boolean }>(FIRMWARE_ACCESS_QUERY);
    return d.adminFirmwareAccess;
  } catch {
    return false;
  }
}

// Загрузка образа — multipart по GraphQL multipart request spec: файл
// байтами, а не base64 (у gql() тело только JSON).
export async function uploadFirmwareImage(
  file: File,
): Promise<FirmwareResult & { image: FirmwareImage | null }> {
  const form = new FormData();
  form.append("operations", JSON.stringify({ query: UPLOAD_MUTATION, variables: { file: null } }));
  form.append("map", JSON.stringify({ "0": ["variables.file"] }));
  form.append("0", file, file.name);
  const token = getToken();
  const resp = await fetch("/graphql", {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: form,
  });
  if (resp.status === 401 || resp.status === 403) {
    logout();
    throw new Error("Сессия истекла");
  }
  if (resp.status === 413) {
    throw new Error("Файл слишком большой — это не образ прошивки.");
  }
  const json = (await resp.json()) as {
    data?: { adminUploadFirmwareImage: FirmwareResult & { image: FirmwareImage | null } };
    errors?: Array<{ message: string }>;
  };
  if (json.errors && json.errors.length > 0) {
    throw new Error(json.errors.map((e) => e.message).join("; "));
  }
  if (!json.data) throw new Error("Пустой ответ сервера");
  return json.data.adminUploadFirmwareImage;
}

// hw в единицах описателя: мажор×100 + минор×10, 720 → «7.2».
export function hwLabel(code: number): string {
  return `${Math.floor(code / 100)}.${Math.floor((code % 100) / 10)}`;
}

export function hwRange(img: FirmwareImage): string {
  return img.hwMin === img.hwMax ? hwLabel(img.hwMin) : `${hwLabel(img.hwMin)}–${hwLabel(img.hwMax)}`;
}

export const PHASE_LABEL: Record<FirmwarePhase, string> = {
  WAITING_CONFIG: "ждёт панель",
  COMMAND_SENT: "команда отправлена",
  DONE: "готово",
  FAILED: "сбой",
  CANCELED: "отменено",
};

// Прошивку обновляют только RTS-панелям подъезда (EvimizPanel, SputnikPanel):
// у Evimiz Light, мониторов и камер свой конфиг. Модель не записана —
// решает Шанырак.
export function firmwarePanel(protocol: string, model: string): boolean {
  return protocol === "rts" && (model === "" || model === "evimiz_panel" || model === "sputnik_panel");
}

export function phaseActive(phase: FirmwarePhase): boolean {
  return phase === "WAITING_CONFIG" || phase === "COMMAND_SENT";
}

const REASON_TEXT: Record<string, string> = {
  BAD_SIZE: "Размер файла не 991232 Б — это не образ прошивки.",
  BAD_CRC: "Контрольная сумма образа не сходится — файл повреждён.",
  BAD_DESCRIPTOR: "У образа нет описателя или он неверный — такой образ не принимается.",
  NAME_TAKEN: "Образ с этой версией уже загружен, и он другой (CRC не совпадает).",
  IMAGE_NOT_FOUND: "Образ не найден — обновите список.",
  DEVICE_NOT_RTS: "Это не RTS-панель.",
  HW_UNKNOWN: "Версия железа панели неизвестна — панель ещё не присылала сведения о себе.",
  HW_MISMATCH: "Образ не подходит к железу панели.",
  DEV_IMAGE_NOT_TESTBENCH: "Dev-образ ставится только на панель с отметкой «стенд».",
  ALREADY_ON_VERSION: "Панель уже на этой версии.",
  UPDATE_IN_PROGRESS: "У панели уже идёт обновление.",
  NOT_CANCELLABLE: "Команда обновления уже ушла — отменить нельзя.",
  NO_UPDATE: "У панели нет идущего обновления.",
};

// Текст отказа: наш — по коду, иначе пояснение Шанырака.
export function failureText(r: FirmwareResult): string {
  if (r.failureReason && REASON_TEXT[r.failureReason]) {
    return r.notice ? `${REASON_TEXT[r.failureReason]} ${r.notice}` : REASON_TEXT[r.failureReason];
  }
  return r.notice || "Шанырак отказал без пояснения.";
}

// Почему образ не подходит панели — подсказка до запуска; окончательно
// решает Шанырак.
export function imageMismatch(img: FirmwareImage, fw: PanelFirmware): string | null {
  if (fw.hwCode === null) return "hw панели неизвестен";
  if (fw.hwCode < img.hwMin || fw.hwCode > img.hwMax) return `не для hw ${hwLabel(fw.hwCode)}`;
  if (img.kind === "DEV" && !fw.testbench) return "dev — нужна отметка «стенд»";
  if (fw.swVersion && fw.swVersion === img.version) return "уже стоит";
  return null;
}

export function formatDateTime(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}
